import React, { useEffect, useState } from 'react';
import { useQuill, useQuillReadOnly } from '../editor/QuillContext';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import {
  PostLayoutSize,
  useIsPostLayoutSize,
} from '../utils/responsive/PostLayoutSizeProvider';
import { usePostLayoutState, useToggleTOC } from './PostLayoutProvider';
import TOCStickyControl from './TOCStickyControl';
import ConnectedTableOfContents from './actionButtons/ConnectedTableOfContents';
import useLayoutSize, { LayoutSize } from './utils/useLayoutSize';

interface PostStickyControlProps {
  postId: string;
  hasBanner: boolean;
}

const PostStickyControl = ({ postId, hasBanner }: PostStickyControlProps) => {
  const [titleLineAnchorElement, setTitleLineAnchorElement] =
    useState<HTMLElement | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isTOCPopoverOpen, setIsTOCPopoverOpen] = useState(false);
  const { quill } = useQuill();
  const readOnly = useQuillReadOnly();
  const toggleTOC = useToggleTOC();
  const { isTOCExpanded, canTOCExpand } = usePostLayoutState();
  const isMobile = useIsPostLayoutSize(PostLayoutSize.mobile);

  useEffect(() => {
    if (!quill || !isMobile) return undefined;
    const titleLine = quill.titleLine?.domNode;

    if (!(titleLine instanceof Element)) return undefined;

    if (!readOnly) {
      setIsTOCPopoverOpen(false);
      return undefined;
    }

    const onTitleClick = () => {
      setIsTOCPopoverOpen((isOpen) => !isOpen);
    };

    setTitleLineAnchorElement(titleLine);
    titleLine.addEventListener('click', onTitleClick);

    return () => {
      titleLine.removeEventListener('click', onTitleClick);
    };
  }, [quill, isMobile, toggleTOC, readOnly]);

  return (
    <ConnectedTableOfContents postId={postId}>
      {(tocProps) => {
        const hasHeaders = tocProps.headers.length > 0;

        if (isMobile) {
          if (!hasHeaders) return null;

          return (
            <ClickOutsideHandler
              onClickOutside={(event) => {
                const titleLine = quill?.scroll.children.head?.domNode;
                if (!titleLine?.contains(event.target as Node)) {
                  setIsTOCPopoverOpen(false);
                }
              }}
            >
              <TOCStickyControl.PopOver
                anchorElement={titleLineAnchorElement}
                open={isTOCPopoverOpen}
                tocState={tocProps}
              />
            </ClickOutsideHandler>
          );
        }

        return (
          <TOCStickyControl.Root expanded={isTOCExpanded} hasBanner={hasBanner}>
            <TOCStickyControl.ActionButtons
              onMouseEnter={() => setIsTOCPopoverOpen(true)}
              onMouseLeave={() => setIsTOCPopoverOpen(false)}
            >
              <TOCStickyControl.Button
                ref={setAnchorElement}
                onClick={() => {
                  if (hasHeaders && canTOCExpand) {
                    toggleTOC();
                  }
                }}
                hasHeaders={hasHeaders}
              />
              {hasHeaders && !canTOCExpand && (
                <TOCStickyControl.PopOver
                  anchorElement={anchorElement}
                  open={isTOCPopoverOpen}
                  tocState={tocProps}
                />
              )}
            </TOCStickyControl.ActionButtons>
            {canTOCExpand && (
              <TOCStickyControl.Content
                onClose={toggleTOC}
                tocProps={tocProps}
              />
            )}
          </TOCStickyControl.Root>
        );
      }}
    </ConnectedTableOfContents>
  );
};

interface TOCInMagneticWallProps {
  postId: string;
  hasBanner: boolean;
}

export const TOCInMagneticWall = ({
  postId,
  hasBanner,
}: TOCInMagneticWallProps) => {
  const { isMagneticWallExpanded } = usePostLayoutState();

  if (!isMagneticWallExpanded) {
    return null;
  }

  return (
    <ConnectedTableOfContents postId={postId}>
      {(tocProps) => (
        <TOCStickyControl.Root alignRight hasBanner={hasBanner} expanded>
          <TOCStickyControl.Content tocProps={tocProps} />
        </TOCStickyControl.Root>
      )}
    </ConnectedTableOfContents>
  );
};

interface PostHistoryViewTOCProps {
  postId: string;
}

export const PostHistoryViewTOC = ({ postId }: PostHistoryViewTOCProps) => {
  const layoutSize = useLayoutSize();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isTOCPopoverOpen, setIsTOCPopoverOpen] = useState(false);
  const { isTOCExpanded } = usePostLayoutState();

  if (
    layoutSize &&
    [LayoutSize.mobile, LayoutSize.tablet].includes(layoutSize)
  ) {
    return null;
  }

  return (
    <ConnectedTableOfContents postId={postId}>
      {(tocProps) => {
        const hasHeaders = tocProps.headers.length > 0;
        if (!hasHeaders) return null;

        return (
          <TOCStickyControl.Root expanded={isTOCExpanded}>
            <TOCStickyControl.ActionButtons
              onMouseEnter={() => setIsTOCPopoverOpen(true)}
              onMouseLeave={() => setIsTOCPopoverOpen(false)}
            >
              {!isTOCExpanded && (
                <>
                  <TOCStickyControl.Button
                    ref={setAnchorElement}
                    hasHeaders={hasHeaders}
                  />
                  <TOCStickyControl.PopOver
                    anchorElement={anchorElement}
                    open={isTOCPopoverOpen}
                    tocState={tocProps}
                  />
                </>
              )}
            </TOCStickyControl.ActionButtons>
            {isTOCExpanded && <TOCStickyControl.Content tocProps={tocProps} />}
          </TOCStickyControl.Root>
        );
      }}
    </ConnectedTableOfContents>
  );
};

export default PostStickyControl;
